import React, { useMemo, useState } from "react";
import Input from "../ui/common/Input";
import Button from "../ui/common/Button";
import { RefreshIcon } from "../../icons/Icons";
import "./ProfileSettings.scss";
import { useAppDispatch } from "../../store";
import { generateApiToken } from "../../store/slices/apiToken";
import { useSelector } from "react-redux";

type ProfileSettingsProps = {
  email: string;
  firstName: string;
  lastName: string;
  newPassword: string;
  confirmNewPassword: string;
  isSuperUserOrAdmin: boolean;
  organisationIdp: string;
  apiTokenAllowed: boolean;
  setFirstName: Function;
  setLastName: Function;
  handleSave: Function;
  setNewPassword: Function;
  setConfirmNewPassword: Function;
};

const ProfileSettings = ({
  email,
  firstName,
  lastName,
  newPassword,
  confirmNewPassword,
  setNewPassword,
  setConfirmNewPassword,
  setFirstName,
  setLastName,
  isSuperUserOrAdmin,
  organisationIdp,
  apiTokenAllowed,
  handleSave,
}: ProfileSettingsProps) => {
  const dispatch = useAppDispatch();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [generatedToken, setGeneratedToken] = useState("");

  const passwordScore = useMemo(() => {
    const passwordMinLength = 8;
    const regexLowerLetter = new RegExp("(?=.*[a-z])");
    const regexUpperLetter = new RegExp("(?=.*[A-Z])");
    const regexDigits = new RegExp("(?=.*[0-9])");
    const regexLength = new RegExp(`(?=.{${passwordMinLength},})`);

    let score = 0;

    if (newPassword.match(regexLowerLetter)) ++score;
    if (newPassword.match(regexUpperLetter)) ++score;
    if (newPassword.match(regexDigits)) ++score;
    if (newPassword.match(regexLength)) ++score;
    if (score === 0 && newPassword.length > 0) ++score;

    return score;
  }, [newPassword]);

  const passwordScoreMessage = useMemo(() => {
    const scoreMessageList = ["", "Weak", "Medium", "Strong", "Very strong"];
    return scoreMessageList[passwordScore];
  }, [passwordScore]);

  const handleGenerateApiToken = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    const confirmed = window.confirm("Are you sure you want to generate a new API token? This will invalidate any existing token.");

    if (confirmed) {
      try {
        const token = await dispatch(generateApiToken());
        setGeneratedToken(token);
        setShowTokenModal(true);
      } catch (error) {
        alert("An error occurred while generating the API token. Please try again later.");
      }
    }
  };

  const handleCloseTokenModal = () => {
    setShowTokenModal(false);
    setGeneratedToken("");
  };

  const passwordsMatching = useMemo(() => {
    if (confirmNewPassword.length > 0) {
      return newPassword === confirmNewPassword;
    }
    return true;
  }, [newPassword, confirmNewPassword]);

  return (
    <div className="profile-settings">
      <h3>Login</h3>
      <Input fullWidth type="text" autoComplete="off" value={email} id="email" disabled />

      {(isSuperUserOrAdmin || organisationIdp === 'smartocto') && (
        <>
          <h3>Change your password</h3>
          <div className="new-password-container">
            <Input
              fullWidth
              id="new-password"
              type={passwordVisible ? "text" : "password"}
              autoComplete="new-password"
              placeholder="new password"
              value={newPassword}
              className={!passwordsMatching ? "error" : ""}
              onChange={(e) => {
                setNewPassword(e.target.value);
              }}
            />
            {newPassword.trim().length > 0 && (
              <div className="password-progress-bar-container">
                <div className={`password-progress-bar password-strength-${passwordScore}`}></div>
                <div className="password-strength-message">{passwordScoreMessage}</div>
              </div>
            )}
          </div>
          <div>
            <Input
              fullWidth
              id="password-retype"
              type={passwordVisible ? "text" : "password"}
              autoComplete="new-password-retype"
              placeholder="retype your password"
              value={confirmNewPassword}
              className={!passwordsMatching ? "error" : ""}
              onChange={(e) => {
                setConfirmNewPassword(e.target.value);
              }}
            />

            {!passwordsMatching && <p className="passwords-matching-message">Passwords are not matching</p>}
          </div>
          <div className="checkbox-container">
            <label htmlFor="show-password">Show password</label>
            <Input
              type="checkbox"
              id="show-password"
              checked={passwordVisible}
              onChange={(e) => {
                setPasswordVisible(e.target.checked);
              }}
            />
          </div>
        </>
      )}

      <h3>Name</h3>
      <div className="input-container">
        <Input
          fullWidth
          type="text"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
          }}
          id="first-name"
          autoComplete="off"
        />
      </div>
      <div className="input-container">
        <Input
          fullWidth
          type="text"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
          }}
          id="last-name"
        />
      </div>

      <h3>API Gateway Token</h3>
      {apiTokenAllowed ? (
        <div className="api-token-section">
          <p className="api-token-description">
            Generate a new API Gateway Token to access the API. The token will only be shown once.
          </p>
          <Button
            variant="success"
            className="generate-api-token-button"
            onClick={(e) => {
              handleGenerateApiToken(e);
            }}
            title="Generate a new API Gateway Token"
          >
            <RefreshIcon /> Generate API Token
          </Button>
        </div>
      ) : (
        <p><em>At the moment you are not allowed to use the API Gateway, contact your administrator</em></p>
      )}

      {showTokenModal && (
        <div className="token-modal-overlay" onClick={handleCloseTokenModal}>
          <div className="token-modal" onClick={(e) => e.stopPropagation()}>
            <div className="token-modal-header">
              <h3>API Token Generated</h3>
              <button className="token-modal-close" onClick={handleCloseTokenModal}>&times;</button>
            </div>
            <div className="token-modal-content">
              <p className="token-warning">
                ⚠️ <strong>Important:</strong> This token will only be shown once. Please copy it now and store it securely.
              </p>
              <p className="token-warning-secondary">
                If you forget this token, you will need to generate a new one.
              </p>
              <div className="token-display">
                <code>{generatedToken}</code>
              </div>
              <Button
                variant="default"
                onClick={() => {
                  navigator.clipboard.writeText(generatedToken);
                  alert("Token copied to clipboard!");
                }}
              >
                Copy to Clipboard
              </Button>
            </div>
            <div className="token-modal-footer">
              <Button variant="default" onClick={handleCloseTokenModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}


      <Button
        fullWidth
        variant="default"
        rounded={false}
        className="profile-details-save-changes"
        onClick={() => {
          handleSave();
        }}
      >
        Save changes
      </Button>
    </div>
  );
};

export default ProfileSettings;
